(() => {
  if (window.__thbHomeV2Loaded) return;
  window.__thbHomeV2Loaded = true;
  const instances = new WeakMap();
  class Home {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.observers = [];
      this.frames = new Set();
      this.dialogTriggers = new Map();
      const signal = this.abort.signal;
      root.querySelectorAll('[data-v2-carousel]').forEach(carousel => this.carousel(carousel));
      root.addEventListener('click', event => {
        const trigger = event.target.closest('[data-v2-hotspot]');
        if (trigger && root.contains(trigger)) {
          const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
          if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;
          event.preventDefault();
          this.dialogTriggers.set(dialog, trigger);
          if (!dialog.open) {
            window.THBModalScroll?.lock(dialog);
            try { dialog.showModal(); } catch { window.THBModalScroll?.unlock(dialog); }
          }
        }
        const close = event.target.closest('[data-v2-dialog-close]');
        if (close) close.closest('dialog')?.close();
      }, {signal});
      root.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-v2-track] > *');
        if (!card) return;
        const track = card.closest('[data-v2-track]');
        const outer = track.getBoundingClientRect(), box = card.getBoundingClientRect();
        const delta = box.left < outer.left ? box.left - outer.left : box.right > outer.right ? box.right - outer.right : 0;
        track.scrollBy({left: delta, behavior: 'instant'});
      }, {signal});
      root.querySelectorAll('.thb-v2-look').forEach(frame => {
        const photo = frame.querySelector('img'), hotspot = frame.querySelector('[data-v2-hotspot]');
        if (!photo || !hotspot) return;
        const position = () => {
          if (!photo.naturalWidth || !photo.naturalHeight) return;
          const box = frame.getBoundingClientRect();
          const scale = Math.max(box.width / photo.naturalWidth, box.height / photo.naturalHeight);
          const styles = getComputedStyle(hotspot);
          const x = parseFloat(styles.getPropertyValue('--hotspot-x')) / 100;
          const y = parseFloat(styles.getPropertyValue('--hotspot-y')) / 100;
          const alignment = getComputedStyle(photo).objectPosition.split(' ');
          const fraction = value => value?.endsWith('%') ? parseFloat(value) / 100 : .5;
          const offsetX = (box.width - photo.naturalWidth * scale) * fraction(alignment[0]);
          const offsetY = (box.height - photo.naturalHeight * scale) * fraction(alignment[1]);
          hotspot.style.setProperty('--hotspot-rendered-x', `${offsetX + x * photo.naturalWidth * scale}px`);
          hotspot.style.setProperty('--hotspot-rendered-y', `${offsetY + y * photo.naturalHeight * scale}px`);
        };
        photo.addEventListener('load', position, {signal});
        if ('ResizeObserver' in window) {
          const observer = new ResizeObserver(position); observer.observe(frame); this.observers.push(observer);
        } else window.addEventListener('resize', position, {signal});
        position();
      });
      root.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        }, {signal});
        dialog.addEventListener('close', () => {
          window.THBModalScroll?.unlock(dialog);
          const trigger = this.dialogTriggers.get(dialog);
          if (trigger?.isConnected) trigger.focus({preventScroll: true});
        }, {signal});
      });
    }
    frame(callback) {
      const id = requestAnimationFrame(() => {this.frames.delete(id); callback();});
      this.frames.add(id);
    }
    carousel(container) {
      const track = container.querySelector('[data-v2-track]');
      const controls = container.querySelector('[data-v2-controls]');
      if (!track || !controls) return;
      const previous = controls.querySelector('[data-v2-prev]');
      const next = controls.querySelector('[data-v2-next]');
      const dots = controls.querySelector('[data-v2-dots]');
      const status = controls.querySelector('[data-v2-page]');
      const signal = this.abort.signal;
      let starts = [0];
      let current = 0;
      let queued = false;
      const children = () => [...track.children].filter(item => item.getBoundingClientRect().width > 0);
      const position = () => Math.abs(track.scrollLeft);
      const update = () => {
        queued = false;
        const items = children();
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        const rtl = getComputedStyle(track).direction === 'rtl';
        const trackBox = track.getBoundingClientRect();
        const offsets = items.map(item => {
          const box = item.getBoundingClientRect();
          return Math.max(0, Math.round((rtl ? trackBox.right - box.right : box.left - trackBox.left) + position()));
        });
        starts = [0];
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const cardWidth = items[0]?.getBoundingClientRect().width || track.clientWidth;
        const perPage = Math.max(1, Math.floor((track.clientWidth + gap + 1) / (cardWidth + gap)));
        offsets.forEach((offset, index) => {
          if (index > 0 && index % perPage === 0 && offset < max) starts.push(offset);
        });
        if (max > 2 && Math.abs(starts[starts.length - 1] - max) > 2) {
          if (starts.length > 1 && max - starts[starts.length - 1] < cardWidth / 2) starts.pop();
          starts.push(max);
        }
        current = starts.reduce((best, value, index) => Math.abs(value-position()) < Math.abs(starts[best]-position()) ? index : best, 0);
        controls.hidden = max <= 2;
        previous.disabled = position() <= 2;
        next.disabled = position() >= max - 2;
        previous.setAttribute('aria-disabled', String(previous.disabled));
        next.setAttribute('aria-disabled', String(next.disabled));
        if (dots.children.length !== starts.length) {
          dots.replaceChildren(...starts.map(() => document.createElement('i')));
        }
        [...dots.children].forEach((dot,index) => dot.setAttribute('aria-current',String(index === current)));
        if (status) status.textContent = (status.dataset.format || '').replace('__index__', String(current+1)).replace('__length__', String(starts.length));
      };
      const schedule = () => {if (!queued) {queued = true; this.frame(update);}};
      const go = direction => {
        update();
        let index;
        if (direction > 0) index = starts.findIndex(start => start > position()+2);
        else {index = starts.length-1; while(index > 0 && starts[index] >= position()-2) index--;}
        if (index < 0) index = starts.length-1;
        const rtl = getComputedStyle(track).direction === 'rtl';
        track.scrollTo({left:starts[index]*(rtl?-1:1),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});
      };
      previous.addEventListener('click', () => go(-1), {signal});
      next.addEventListener('click', () => go(1), {signal});
      track.addEventListener('scroll', schedule, {signal,passive:true});
      track.addEventListener('focusin', event => {
        const card = children().find(item => item.contains(event.target));
        if (!card) return;
        const outer = track.getBoundingClientRect();
        const box = card.getBoundingClientRect();
        const delta = box.left < outer.left ? box.left-outer.left : box.right > outer.right ? box.right-outer.right : 0;
        if (Math.abs(delta) > 1) track.scrollBy({left:delta,behavior:'instant'});
        schedule();
      }, {signal});
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(schedule);
        observer.observe(track);
        this.observers.push(observer);
      } else window.addEventListener('resize', schedule, {signal});
      update();
    }
    destroy() {
      this.abort.abort();
      this.observers.forEach(observer => observer.disconnect());
      this.frames.forEach(id => cancelAnimationFrame(id));
      this.root.querySelectorAll('dialog[open]').forEach(dialog => { dialog.close(); window.THBModalScroll?.unlock(dialog); });
    }
  }
  const rootsIn = scope => {
    const roots = [...scope.querySelectorAll('[data-thb-v2]')];
    if (scope.matches?.('[data-thb-v2]')) roots.unshift(scope);
    return roots;
  };
  const init = scope => rootsIn(scope).forEach(root => {if (!instances.has(root)) instances.set(root,new Home(root));});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once:true});
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    rootsIn(event.target).forEach(root => {instances.get(root)?.destroy(); instances.delete(root);});
  });
})();
